// Geboortevenster · Data: exacte segmenten van POST /api/humandesign/birth-window (sessiecache) en de schatting voor oude documenten zonder snelheden.
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PlatformCalculateChartRequest } from '../../../data/_generated-api/platform-api/models';
import { PlatformHumanDesignService } from '../../../data/_generated-api/platform-api/services/platform-human-design.service';
import { HdBodygraphChartPreset } from '../bodygraph/hd-bodygraph.models';
import { HdChartLibraryService } from '../library/hd-chart-library.service';
import { extractCycleSourceCore } from '../library/hd-cycle-chart.utils';
import { estimateBirthWindowData } from './birth-window-estimate';
import { HdBirthWindowBodySegments, HdBirthWindowData, HdBirthWindowSegment, HdBirthWindowSide } from './birth-window.models';

/** Structurele vorm van de backend-respons (`BirthWindowResponse`, camelCase). */
interface BirthWindowResponseShape {
  readonly birthUtc?: string | null;
  readonly fromUtc?: string | null;
  readonly toUtc?: string | null;
  readonly sides?: readonly {
    readonly side?: string | null;
    readonly bodies?: readonly {
      readonly planetId?: number | null;
      readonly sourceKey?: string | null;
      readonly segments?: readonly {
        readonly fromUtc?: string | null;
        readonly toUtc?: string | null;
        readonly gate?: number | null;
        readonly line?: number | null;
        readonly color?: number | null;
        readonly tone?: number | null;
        readonly base?: number | null;
      }[] | null;
    }[] | null;
  }[] | null;
}

/** `/calculate`-request voor een (verschoven) geboortemoment met de locatie en tijdzone van de preset. */
export function buildBirthWindowCalculateRequest(preset: HdBodygraphChartPreset, utcIso: string): PlatformCalculateChartRequest {
  const core = extractCycleSourceCore(preset.birthData);
  const location = (preset.birthData as { location?: { city?: string | null; country?: string | null } }).location ?? {};
  return {
    name: preset.name,
    utc: utcIso,
    timezone: core.timezone || 'UTC',
    location: { city: location.city ?? null, country: location.country ?? null, lat: core.latitude, lon: core.longitude },
  };
}

export function mapBirthWindowResponse(response: BirthWindowResponseShape): HdBirthWindowData {
  const bodies: HdBirthWindowBodySegments[] = [];
  for (const side of response.sides ?? []) {
    const sideKey: HdBirthWindowSide = side.side === 'design' ? 'design' : 'personality';
    for (const body of side.bodies ?? []) {
      const segments: HdBirthWindowSegment[] = (body.segments ?? []).map(segment => {
        const base = {
          fromMs: Date.parse(segment.fromUtc ?? ''),
          toMs: Date.parse(segment.toUtc ?? ''),
          gate: segment.gate ?? 0,
          line: segment.line ?? 0,
        };
        if (segment.color == null || segment.tone == null) return base;
        return segment.base != null
          ? { ...base, color: segment.color, tone: segment.tone, base: segment.base }
          : { ...base, color: segment.color, tone: segment.tone };
      });
      bodies.push({ side: sideKey, planetId: body.planetId ?? 0, sourceKey: body.sourceKey ?? '', segments });
    }
  }
  return {
    birthMs: Date.parse(response.birthUtc ?? ''),
    fromMs: Date.parse(response.fromUtc ?? ''),
    toMs: Date.parse(response.toUtc ?? ''),
    bodies,
  };
}

/**
 * Data voor het geboortevenster: exacte segmenten van de backend (per
 * geboorte-UTC en vensterbreedte gecachet in de sessie, in-flight gedeeld)
 * en de eerste-orde-schatting voor de indicator (snelheden uit het document;
 * oude documenten zonder snelheden halen één keer een vers document op).
 */
@Injectable({ providedIn: 'root' })
export class HdBirthWindowDataService {
  private readonly api = inject(PlatformHumanDesignService);
  private readonly library = inject(HdChartLibraryService);
  private readonly windows = new Map<string, Promise<HdBirthWindowData>>();
  private readonly freshDocuments = new Map<string, Promise<HdBodygraphChartPreset | undefined>>();

  getWindow(preset: HdBodygraphChartPreset, halfWindowMinutes: number): Promise<HdBirthWindowData> {
    const utc = extractCycleSourceCore(preset.birthData).birthDateUtc;
    if (!utc) return Promise.reject(new Error('Deze chart heeft geen geboortemoment.'));
    const key = `${utc}|${halfWindowMinutes}`;
    const cached = this.windows.get(key);
    if (cached) return cached;
    const request = firstValueFrom(this.api.postHumandesignBirthWindow({ body: { utc, windowMinutes: halfWindowMinutes } }))
      .then(response => mapBirthWindowResponse(response as unknown as BirthWindowResponseShape))
      .catch(error => {
        this.windows.delete(key);
        throw error;
      });
    this.windows.set(key, request);
    return request;
  }

  /**
   * Niveau A: schatting uit `longitudeSpeed`. Ontbreken de snelheden, dan één
   * `calculateFromUtc` (per geboorte-UTC gecachet); lukt ook dat niet → undefined.
   */
  async getEstimate(preset: HdBodygraphChartPreset, halfWindowMs: number): Promise<HdBirthWindowData | undefined> {
    const direct = estimateBirthWindowData(preset, halfWindowMs);
    if (direct) return direct;
    const utc = extractCycleSourceCore(preset.birthData).birthDateUtc;
    if (!utc) return undefined;
    let pending = this.freshDocuments.get(utc);
    if (!pending) {
      pending = this.library.calculateFromUtc(buildBirthWindowCalculateRequest(preset, utc)).catch(() => undefined);
      this.freshDocuments.set(utc, pending);
    }
    const fresh = await pending;
    if (!fresh) {
      this.freshDocuments.delete(utc);
      return undefined;
    }
    return estimateBirthWindowData({ ...fresh, birthData: preset.birthData }, halfWindowMs);
  }

  clear(): void {
    this.windows.clear();
    this.freshDocuments.clear();
  }
}
